import React, { FC, useEffect, useState, useRef } from 'react';
import { useParams } from 'react-router-dom';
import { CallRecordingWrapper } from './CallRecording.styled';
import { getCallRecording } from '../../api/helpers';
import { Calendar, Users } from 'lucide-react';

interface CallRecordingProps {}

interface TranscriptData {
  _id: string;
  call_date: string;
  transcript: string;
}

interface CallData {
  _id: string;
  attendees_list: string[];
  bot_id: string;
  call_date: string;
  client_meeting_id: string;
  client_meeting_uuid: string | null;
  deal_id: string;
  join_url: string;
  medium: string;
  meeting_name: string;
  new_deal: boolean;
  organization_id: string;
  record: boolean;
  transcript: TranscriptData;
  timestamp: string;
  updated_at: string;
  user_id: string;
  user_email?: string;
  video_key: string;
  video_url?: string;
}

interface TranscriptLine {
  start: number;
  end: number;
  speaker: string;
  content: string;
}

// Define an array of 10 random colors for speaker avatars
const avatarColors = [
  "#E8A6A6",
  "#D6B497",
  "#A6D8A6",
  "#A6CDE8",
  "#F1A6E8",
  "#D1A6F1",
  "#A6F1E8",
  "#F1E8A6",
  "#C6F1A6",
  "#F1C0A6",
];

// Function to get a consistent color for each speaker
function getColorForSpeaker(speaker: string): string {
  // Use a simple hash function to get a consistent index based on the speaker's name
  let hash = 0;
  for (let i = 0; i < speaker.length; i++) {
    hash = speaker.charCodeAt(i) + ((hash << 5) - hash);
  }
  // Return a color from the array using the hash
  const colorIndex = Math.abs(hash % avatarColors.length);
  return avatarColors[colorIndex];
}

const CallRecording: FC<CallRecordingProps> = () => {
  const { botId } = useParams<{ botId: string }>();
  const [loading, setLoading] = useState<boolean>(true);
  const [callData, setCallData] = useState<CallData | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [parsedTranscript, setParsedTranscript] = useState<TranscriptLine[]>([]);
  const [searchText, setSearchText] = useState<string>('');
  const [currentTime, setCurrentTime] = useState<string>('00:00');
  const [duration, setDuration] = useState<string>('00:00');
  const [videoDuration, setVideoDuration] = useState<number>(0);
  const [videoError, setVideoError] = useState<string | null>(null);
  const [videoUrl, setVideoUrl] = useState<string | null>(null);
  
  const textContainerRef = useRef<HTMLDivElement | null>(null);
  const activeLineRef = useRef<HTMLDivElement | null>(null);
  const videoRef = useRef<HTMLVideoElement | null>(null);
  const progressRef = useRef<HTMLInputElement | null>(null);
  
  useEffect(() => {
    const fetchCallRecording = async () => {
      try {
        setLoading(true);
        const response = await getCallRecording(botId as string);
        if (response && response.status === 'success') {
          setCallData(response.call);
          
          // Set the video URL
          if (response.call.video_url) {
            // Use the video_url field from the response
            setVideoUrl(response.call.video_url);
          }
          
          setError(null);
        } else {
          setError(response?.message || 'Failed to load call recording data');
          setCallData(null);
        }
      } catch (err) {
        console.error('Error fetching call recording:', err);
        setError('Failed to load call recording data');
        setCallData(null);
      } finally {
        setLoading(false);
      }
    };
    
    if (botId) {
      fetchCallRecording();
    }
  }, [botId]);
  
  // Parse transcript when call data is loaded
  useEffect(() => {
    if (callData?.transcript?.transcript) {
      const parsed = parseTranscript(callData.transcript.transcript);
      setParsedTranscript(parsed);
    }
  }, [callData]);
  
  // Function to parse time from timestamp format
  function parseTime(time: string): number {
    const [minutes, seconds] = time.split(":").map(parseFloat);
    return minutes * 60 + seconds;
  }
  
  // Function to parse the transcript text into structured data
  function parseTranscript(input: string): TranscriptLine[] {
    const linePattern = /\[(\d{2}:\d{2}\.\d{2})\] ([^:]+): (.+?) \[(\d{2}:\d{2}\.\d{2})\]/g;
    const lines: TranscriptLine[] = [];
    let match: RegExpExecArray | null;

    while ((match = linePattern.exec(input)) !== null) {
      const [_, start, speaker, content, end] = match;
      lines.push({
        start: parseTime(start),
        end: parseTime(end),
        speaker,
        content,
      });
    }

    const combined: TranscriptLine[] = [];
    let current: TranscriptLine | null = null;

    for (const line of lines) {
      if (current && current.speaker === line.speaker) {
        current.content += " " + line.content;
        current.end = line.end;
      } else {
        if (current) combined.push(current);
        current = { ...line };
      }
    }
    if (current) combined.push(current);
    return combined;
  }
  
  // Function to format time display
  function formatTime(seconds: number): string {
    const minutes = Math.floor(seconds / 60);
    const remainingSeconds = Math.floor(seconds % 60);
    return `${minutes < 10 ? '0' : ''}${minutes}:${remainingSeconds < 10 ? "0" : ""}${remainingSeconds}`;
  }
  
  // Function to format timestamp for display
  function formatTimestamp(seconds: number): string {
    return `00:${formatTime(seconds)}`;
  }
  
  // Function to jump to a specific time in the video
  const jumpTo = (time: number) => {
    if (videoRef.current) {
      videoRef.current.currentTime = time;
      videoRef.current.play().catch(err => {
        console.error("Error playing video:", err);
      });
    }
  };
  
  // Handle video time updates
  useEffect(() => {
    const videoElement = videoRef.current;
    if (!videoElement) return;

    const handleTimeUpdate = () => {
      const current = videoElement.currentTime;
      setCurrentTime(formatTime(current));
      
      if (progressRef.current) {
        progressRef.current.value = (current / videoElement.duration * 100).toString();
      }

      const activeLine = parsedTranscript.find(
        (line) => current >= line.start && current <= line.end
      );

      if (activeLine && textContainerRef.current) {
        const lineId = `line-${activeLine.start.toFixed(2).replace(".", "_")}`;
        const transcriptElement = textContainerRef.current.querySelector(`#${lineId}`);
        
        // Remove highlight from previous line
        if (activeLineRef.current && activeLineRef.current !== transcriptElement) {
          activeLineRef.current.classList.remove('active-line');
        }
        
        // Add highlight to current line and scroll to it
        if (transcriptElement && transcriptElement !== activeLineRef.current) {
          transcriptElement.classList.add('active-line');
          activeLineRef.current = transcriptElement as HTMLDivElement;
          
          // Use scrollIntoView but contain the scrolling effect to the transcript container
          const container = textContainerRef.current;
          const currentScrollPosition = window.scrollY; // Save current page scroll position
          
          transcriptElement.scrollIntoView({
            behavior: "smooth",
            block: "center",
          });
          
          // Restore the previous scroll position of the window
          window.scrollTo({
            top: currentScrollPosition,
            behavior: 'auto'
          });
        }
      }
    };

    const handleLoadedMetadata = () => {
      setDuration(formatTime(videoElement.duration));
      setVideoDuration(videoElement.duration);
      setVideoError(null);
    };
    
    const handleError = (e: Event) => {
      console.error("Video error:", e);
      setVideoError("Error loading video. The format may not be supported or the file may be missing.");
    };

    videoElement.addEventListener("timeupdate", handleTimeUpdate);
    videoElement.addEventListener("loadedmetadata", handleLoadedMetadata);
    videoElement.addEventListener("error", handleError);
    
    return () => {
      videoElement.removeEventListener("timeupdate", handleTimeUpdate);
      videoElement.removeEventListener("loadedmetadata", handleLoadedMetadata);
      videoElement.removeEventListener("error", handleError);
    };
  }, [parsedTranscript]);
  
  // Handle progress bar changes
  const handleProgressChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (videoRef.current) {
      const newTime = parseFloat(e.target.value) / 100 * videoRef.current.duration;
      videoRef.current.currentTime = newTime;
    }
  };
  
  // Handle video playback
  const togglePlayPause = () => {
    if (videoRef.current) {
      if (videoRef.current.paused) {
        videoRef.current.play().catch(err => {
          console.error("Error playing video:", err);
          setVideoError("Could not play video. The format may not be supported or the file may be missing.");
        });
      } else {
        videoRef.current.pause();
      }
    }
  };
  
  // Function to search in transcript
  const handleSearch = (e: React.FormEvent) => {
    e.preventDefault();
    if (!textContainerRef.current || !searchText.trim()) return;
    
    const container = textContainerRef.current;
    const transcriptLines = container.querySelectorAll('.transcript-line-content');
    let found = false;
    
    // Reset previous highlights
    document.querySelectorAll('.highlight-search').forEach(el => {
      const text = el.textContent || '';
      const parent = el.parentNode;
      if (parent) {
        parent.textContent = text;
      }
    });
    
    for (let i = 0; i < transcriptLines.length; i++) {
      const line = transcriptLines[i];
      const lineText = line.textContent || '';
      const searchIndex = lineText.toLowerCase().indexOf(searchText.toLowerCase());
      
      if (searchIndex >= 0) {
        const lineElement = line.closest('.transcript-line');
        
        if (lineElement && !found) {
          // Save current page scroll position
          const currentScrollPosition = window.scrollY;
          
          lineElement.scrollIntoView({ behavior: 'smooth', block: 'center' });
          
          // Restore the previous scroll position of the window
          window.scrollTo({
            top: currentScrollPosition,
            behavior: 'auto'
          });
          
          found = true;
        }
        
        const beforeMatch = lineText.substring(0, searchIndex);
        const match = lineText.substring(searchIndex, searchIndex + searchText.length);
        const afterMatch = lineText.substring(searchIndex + searchText.length);
        
        line.innerHTML = `${beforeMatch}<span class="highlight-search">${match}</span>${afterMatch}`;
      }
    }
  };
  
  const formatDate = (dateString: string) => {
    try {
      const date = new Date(dateString);
      
      // Get month name
      const months = ['January', 'February', 'March', 'April', 'May', 'June', 
                     'July', 'August', 'September', 'October', 'November', 'December'];
      const month = months[date.getMonth()];
      
      // Get day with ordinal suffix
      const day = date.getDate();
      let dayStr = day.toString();
      
      if (day % 10 === 1 && day !== 11) {
        dayStr += 'st';
      } else if (day % 10 === 2 && day !== 12) {
        dayStr += 'nd';
      } else if (day % 10 === 3 && day !== 13) {
        dayStr += 'rd';
      } else {
        dayStr += 'th';
      }
      
      // Get year
      const year = date.getFullYear();
      
      // Format time with AM/PM
      let hours = date.getHours();
      const minutes = date.getMinutes().toString().padStart(2, '0');
      const ampm = hours >= 12 ? 'PM' : 'AM';
      hours = hours % 12;
      hours = hours ? hours : 12; // Convert 0 to 12 for 12 AM
      
      return `${month} ${dayStr}, ${year}, ${hours}:${minutes} ${ampm}`;
    } catch (e) {
      return dateString;
    }
  };
  
  // Log the videoUrl for debugging
  useEffect(() => {
    console.log('Video URL set to:', videoUrl);
  }, [videoUrl]);
  
  return (
    <CallRecordingWrapper>
      <div className="recording-header">
        <div className="meeting-icon">
          <Calendar />
        </div>
        <div className="meeting-info">
          <h2>{callData?.meeting_name || 'Call Recording'}</h2>
          <div className="meeting-meta">
            <span className="meeting-date">{callData ? formatDate(callData.call_date) : ''}</span>
            <span className="meeting-duration">Duration: {duration}</span>
          </div>
        </div>
      </div>
      
      {loading && <p className="loading-message">Loading call data...</p>}
      
      {error && <p className="error">{error}</p>}
      
      {!loading && callData && (
        <div className="recording-container">
          <div className="main-content">
            {videoUrl && (
              <div className="video-container">
                <video ref={videoRef} src={videoUrl} controls />
                {videoError && <div className="video-error">{videoError}</div>}
              </div>
            )}
            
            <div className="transcript-container">
              <div className="transcript-header">
                <h3>Transcript</h3>
                {/* <form className="search-form" onSubmit={handleSearch}>
                  <input
                    type="text"
                    placeholder="Search transcript..."
                    value={searchText}
                    onChange={(e) => setSearchText(e.target.value)}
                  />
                  <button type="submit">Search</button>
                </form> */}
              </div>
              
              <div className="transcript-content" ref={textContainerRef}>
                {parsedTranscript.length > 0 ? (
                  parsedTranscript.map((line) => (
                    <div 
                      key={line.start} 
                      id={`line-${line.start.toFixed(2).replace(".", "_")}`}
                      className="transcript-line"
                      onClick={() => jumpTo(line.start)}
                    >
                      <div className="transcript-line-left">
                        <div className="avatar">
                          {line.speaker[0].toUpperCase()}
                        </div>
                      </div>
                      <div className="transcript-line-right">
                        <div className="transcript-line-header">
                          <span className="speaker-name">{line.speaker}</span>
                          <span className="timestamp">{formatTimestamp(line.start)}</span>
                        </div>
                        <div className="transcript-line-content">{line.content}</div>
                      </div>
                    </div>
                  ))
                ) : (
                  <div className="raw-transcript">
                    {callData.transcript.transcript.split('\n').map((line, index) => (
                      <div key={index} className="raw-line">{line}</div>
                    ))}
                  </div>
                )}
              </div>
            </div>
          </div>
          
          <div className="attendees-container">
            <h3><Users /> Attendees ({callData.attendees_list.length + (callData.user_email ? 1 : 0)})</h3>
            <div className="attendees-list">
              {/* Add user_email to the top of the list if it exists */}
              {callData.user_email && (
                <div className="attendee">
                  <div className="attendee-avatar" style={{ backgroundColor: getColorForSpeaker(callData.user_email) }}>
                    {callData.user_email.split('@')[0].split('.').map(part => 
                      part.charAt(0).toUpperCase()
                    ).join('')}
                  </div>
                  <div className="attendee-info">
                    <div className="attendee-name">
                      {callData.user_email.split('@')[0].split('.').map(part => 
                        part.charAt(0).toUpperCase() + part.slice(1)
                      ).join(' ')}
                    </div>
                    <div className="attendee-email">{callData.user_email}</div>
                  </div>
                </div>
              )}
              
              {callData.attendees_list.map((attendee, index) => {
                const name = attendee.split('@')[0].split('.').map(part => 
                  part.charAt(0).toUpperCase() + part.slice(1)
                ).join(' ');
                
                return (
                  <div key={index} className="attendee">
                    <div className="attendee-avatar">
                      {name.split(' ').map(part => part[0]).join('')}
                    </div>
                    <div className="attendee-info">
                      <div className="attendee-name">{name}</div>
                      <div className="attendee-email">{attendee}</div>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        </div>
      )}
    </CallRecordingWrapper>
  );
};

export default CallRecording;
